import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PropertyProvider } from "./context/PropertyContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminSetup from "./pages/AdminSetup";
import { Privacy, Terms } from "./pages/marketing/Legal";
import Home from "./pages/marketing/Home";
import Today from "./pages/desk/Today";
import DailyRun from "./pages/desk/DailyRun";
import Inbox from "./pages/desk/Inbox";
import Followups from "./pages/desk/Followups";
import Reviews from "./pages/desk/Reviews";
import Guests from "./pages/desk/Guests";
import Properties from "./pages/desk/Properties";
import Reports from "./pages/desk/Reports";
import Settings from "./pages/desk/Settings";
import Demo from "./pages/desk/Demo";
import ContentArchive from "./pages/desk/ContentArchive";
import GuestHub from "./pages/guesthub/GuestHub";
import GuestHubRequestPage from "./pages/guesthub/GuestHubRequestPage";
import RequestReview from "./pages/marketing/RequestReview";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading, setRedirectPath } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center">
        <div className="animate-pulse">
          <div className="text-2xl font-heading text-[#3F617E]">Maris Desk</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    // Save the current path for redirect after login
    setRedirectPath(window.location.pathname);
    return <Navigate to="/desk/login" replace />;
  }
  
  return <PropertyProvider>{children}</PropertyProvider>;
};

// Admin-only route guard — wraps ProtectedRoute and additionally requires
// `user.role === 'ADMIN'`. Non-admin sessions are redirected to /desk/today
// rather than shown a forbidden screen, since the route should feel like it
// simply isn't part of their workspace. Backend routes are still independently
// guarded with `require_admin`, so direct API calls are also rejected.
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/desk/login" replace />;
  }
  if (user.role !== 'ADMIN') {
    return <Navigate to="/desk/today" replace />;
  }
  return <PropertyProvider>{children}</PropertyProvider>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* ------------------------------------------------------------------
       * Public routes (kept at root for backwards-compat with bookmarks,
       * password-reset emails, GuestHub links, etc.)
       * ------------------------------------------------------------------ */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin-setup" element={<AdminSetup />} />
      <Route path="/request-review" element={<RequestReview />} />
      {/* Public homepage is the front door. /home kept as redirect for legacy
       * links / bookmarks / emails / external references. */}
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* GuestHub public routes */}
      <Route path="/guesthub/:propertySlug" element={<GuestHubRequestPage />} />
      <Route path="/g/:slug" element={<GuestHub />} />
      <Route path="/g/:slug/checkin" element={<GuestHub />} />
      <Route path="/g/:slug/guide" element={<GuestHub />} />
      <Route path="/g/:slug/recommendations" element={<GuestHub />} />
      <Route path="/g/:slug/requests" element={<GuestHub />} />
      <Route path="/g/:slug/feedback" element={<GuestHub />} />
      
      {/* ------------------------------------------------------------------
       * Protected desk routes — original paths (DO NOT remove or change in
       * Phase 1; existing operator bookmarks and sidebar links depend on
       * these.)
       * ------------------------------------------------------------------ */}
      <Route path="/desk/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
      <Route path="/desk/daily-run" element={<ProtectedRoute><DailyRun /></ProtectedRoute>} />
      <Route path="/desk/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
      <Route path="/desk/followups" element={<ProtectedRoute><Followups /></ProtectedRoute>} />
      <Route path="/desk/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />
      <Route path="/desk/guests" element={<ProtectedRoute><Guests /></ProtectedRoute>} />
      <Route path="/desk/properties" element={<ProtectedRoute><Properties /></ProtectedRoute>} />
      <Route path="/desk/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/desk/settings" element={<AdminRoute><Settings /></AdminRoute>} />
      <Route path="/desk/demo" element={<ProtectedRoute><Demo /></ProtectedRoute>} />
      <Route path="/desk/content-archive" element={<AdminRoute><ContentArchive /></AdminRoute>} />

      {/* ------------------------------------------------------------------
       * Phase 1 — Back-of-house route dual-mount (2026-06-21).
       * These are the future canonical paths. Each renders the same
       * component as the original path so both URLs serve the same screen
       * with no redirect — current bookmarks keep working, new URLs work
       * for forward-facing comms (operator handoffs, docs, etc.).
       * Sidebar and ProtectedRoute still use the original paths in
       * Phase 1; the rename will happen in Phase 2 with the user's
       * approval.
       * ------------------------------------------------------------------ */}
      {/* /desk root → render Today (same as authenticated home) */}
      <Route path="/desk" element={<ProtectedRoute><Today /></ProtectedRoute>} />
      {/* Auth aliases under /desk (safety net for future emails / shortcuts) */}
      <Route path="/desk/login" element={<Login />} />
      <Route path="/desk/forgot-password" element={<ForgotPassword />} />
      <Route path="/desk/reset-password" element={<ResetPassword />} />
      {/* Renamed canonical paths */}
      <Route path="/desk/guest-inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
      <Route path="/desk/follow-ups" element={<ProtectedRoute><Followups /></ProtectedRoute>} />
      <Route path="/desk/client-workspaces" element={<ProtectedRoute><Properties /></ProtectedRoute>} />

      {/* Default redirect — unknown URLs land on the public homepage, NOT
       * the backend. Authenticated users navigate to /desk/* explicitly. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
